import { useState } from 'react';

import { uiConfig } from '@/config';
import { cn } from '@/libs';
import { FieldError } from '../../core';
import { FormFieldType, SingleFormField } from '../../typings';
import {
  FACILITY_FREE_TEXT_WARNING,
  isFacilityLookup,
} from '../../utils/facilityFreeText';
import { Checkbox } from '../inputs';
import {
  useClearOnNotApplicable,
  useDisabledWhenToggled,
  useExternalLookupOptions,
  useFieldWarning,
  useNotApplicableField,
  usePointerReleaseDeferred,
  useShouldValidateField,
} from '../../hooks';
import { FormFieldMapper as AnyFormFieldMapper } from '..';

import { SingleFieldInnerPresenter } from './single-field';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';
import { FieldWarningAlert } from './FieldWarningAlert';

import './SingleFieldPresenter.scss';

type SingleFieldPresenterProps = {
  field: SingleFormField;
  disabled?: boolean;
};

/**
 * @internal
 * This component is for internal use only.
 *
 * SingleFieldMapper is a functional component that takes field properties and
 * returns the appropriate form field based on the field type.
 */
export const SingleFieldPresenter: React.FC<SingleFieldPresenterProps> = ({
  field,
  disabled,
}) => {
  const lookupOptions = useExternalLookupOptions(field.fieldId);
  const isFacility = isFacilityLookup(lookupOptions);

  const containerClasses = field.size
    ? cn(
        'sf__container',
        uiConfig.grid.maxColumnsSpanClass,
        { 'sf__container--checkbox': field.type === FormFieldType.Checkbox },
        { 'sf__container--facility': isFacility },
        Object.entries(field.size).map(([key, value]) => ({
          [`${key}:col-span-${value}`]: value !== undefined,
        })),
      )
    : undefined;

  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);

  // Manage shouldValidate state for fields with NA checkbox
  useShouldValidateField(field.fieldId, !isFieldNA);

  useClearOnNotApplicable(field, isFieldNA);

  const isDisabledByToggle = useDisabledWhenToggled(field);

  const warning = useFieldWarning(field);
  const isFreeText = warning === FACILITY_FREE_TEXT_WARNING;
  // Not Applicable, on the field or its group, hides it; read-only doesn't, since it only informs.
  const showsFreeText = usePointerReleaseDeferred(
    isFreeText && !isFieldNA && !disabled,
  );
  const seededWarning = isFreeText || isFieldNA ? undefined : warning;
  const shownWarning = showsFreeText ? FACILITY_FREE_TEXT_WARNING : seededWarning;

  // Only a change after mount is announced, so a reload already holding typed text stays quiet.
  const [mountedWithFreeText] = useState(showsFreeText);
  const [freeTextChanged, setFreeTextChanged] = useState(false);
  if (!freeTextChanged && showsFreeText !== mountedWithFreeText) {
    setFreeTextChanged(true);
  }
  const announcement =
    freeTextChanged && showsFreeText ? FACILITY_FREE_TEXT_WARNING.title : '';

  if ('hidden' in field && field.hidden) {
    return null;
  }

  return (
    <div className={containerClasses}>
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}
      <SingleFieldInnerPresenter
        field={field}
        disabled={isFieldNA || disabled || isDisabledByToggle}
      />

      {/* Group restriction guarding this field — shown here, not at group level. */}
      {field.restrictionErrorKey && (
        <FieldError name={field.restrictionErrorKey} />
      )}

      {field.notApplicableField !== undefined && (
        <Checkbox
          name={field.notApplicableField.fieldId}
          label={field.notApplicableField.label}
          size="small"
        />
      )}

      {isFieldNA && field.naDetails && (
        <AnyFormFieldMapper field={field.naDetails} disabled={disabled} />
      )}

      {/* Last visible child, so the notice sits under the whole field rather than between the
          input and the Not Applicable checkbox that belongs beside it. */}
      {shownWarning && <FieldWarningAlert warning={shownWarning} />}

      {/* Mounted with the field and empty at first, so screen readers hear the warning turn on. */}
      {isFacility && (
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      )}
    </div>
  );
};
